import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';
import {
  deleteVpsHostingRequest,
  getVpsHostingRequests,
} from '../api/vpsHostingRequestsApi';
import ConfirmDialog from '../components/dashboard/ConfirmDialog';

const SERVER_LOCATION_OPTIONS = [
  { value: '', label: 'All locations' },
  { value: 'New York', label: 'New York' },
  { value: 'London', label: 'London' },
  { value: 'Frankfurt', label: 'Frankfurt' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Tokyo', label: 'Tokyo' },
];

const SERVER_TYPE_OPTIONS = [
  { value: '', label: 'All server types' },
  { value: 'Essential VPS', label: 'Essential VPS' },
  { value: 'Professional VPS', label: 'Professional VPS' },
  { value: 'Performance VPS', label: 'Performance VPS' },
];

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const VpsHostingRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [serverLocation, setServerLocation] = useState('');
  const [serverType, setServerType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadRequests = async () => {
    setIsLoading(true);
    setError('');

    try {
      const data = await getVpsHostingRequests();
      setRequests(data);
    } catch (err) {
      setRequests([]);
      setError(err.message || 'Failed to load requests.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return requests.filter((request) => {
      if (serverLocation && request.serverLocation !== serverLocation) {
        return false;
      }

      if (serverType && request.serverType !== serverType) {
        return false;
      }

      if (query) {
        const haystack = `${request.fullName} ${request.email} ${request.messagePreview}`.toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }

      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`);
        if (new Date(request.createdAt) < from) {
          return false;
        }
      }

      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59.999`);
        if (new Date(request.createdAt) > to) {
          return false;
        }
      }

      return true;
    });
  }, [requests, searchQuery, serverLocation, serverType, dateFrom, dateTo]);

  const hasActiveFilters =
    searchQuery || serverLocation || serverType || dateFrom || dateTo;

  const clearFilters = () => {
    setSearchQuery('');
    setServerLocation('');
    setServerType('');
    setDateFrom('');
    setDateTo('');
  };

  const handleDeleteClick = (request) => {
    setRequestToDelete(request);
  };

  const handleDeleteCancel = () => {
    if (deletingId) {
      return;
    }

    setRequestToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!requestToDelete) {
      return;
    }

    setDeletingId(requestToDelete.id);
    setError('');

    try {
      await deleteVpsHostingRequest(requestToDelete.id);
      setRequests((current) => current.filter((item) => item.id !== requestToDelete.id));
      setRequestToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete request.');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading requests…</p>;
  }

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
          Submissions
        </p>
        <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
          VPS hosting requests
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#6b635c]">
          Review, filter, and manage VPS hosting requests submitted from the public form.
        </p>
      </header>

      {error ? (
        <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <section className="mb-5 rounded-md border border-[#e4d9c8] bg-white p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium text-[#130705]">Filters</p>
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[13px] font-medium text-[#785E32] hover:underline"
            >
              Clear filters
            </button>
          ) : null}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <label className="block md:col-span-2 xl:col-span-1">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Search</span>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Name, email, or message"
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">
              Server location
            </span>
            <select
              value={serverLocation}
              onChange={(event) => setServerLocation(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            >
              {SERVER_LOCATION_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Server type</span>
            <select
              value={serverType}
              onChange={(event) => setServerType(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            >
              {SERVER_TYPE_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">From date</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">To date</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>
        </div>

        <p className="mt-4 text-[12px] text-[#9a9288]">
          Showing {filteredRequests.length} of {requests.length} requests
        </p>
      </section>

      {requests.length === 0 ? (
        <p className="text-sm text-[#6b635c]">No requests yet.</p>
      ) : filteredRequests.length === 0 ? (
        <p className="text-sm text-[#6b635c]">No requests match the current filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[#e4d9c8] bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-[#e4d9c8] bg-[#faf8f4] text-[12px] uppercase tracking-[0.12em] text-[#6b635c]">
              <tr>
                <th className="px-4 py-3 font-medium">Applicant</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Location</th>
                <th className="px-4 py-3 font-medium">Server type</th>
                <th className="px-4 py-3 font-medium">Submitted</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request) => (
                <tr key={request.id} className="border-b border-[#f0e8dc] last:border-b-0">
                  <td className="px-4 py-3 font-medium text-[#130705]">{request.fullName}</td>
                  <td className="px-4 py-3 text-[#4a4340]">{request.email}</td>
                  <td className="px-4 py-3 text-[#4a4340]">{request.serverLocation}</td>
                  <td className="px-4 py-3 text-[#4a4340]">{request.serverType}</td>
                  <td className="px-4 py-3 text-[#6b635c]">{formatDate(request.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/dashboard/vps-hosting-requests/${request.id}`}
                        className="inline-flex items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-1.5 text-[12px] font-medium text-[#785E32] transition hover:bg-[#faf8f4]"
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(request)}
                        disabled={deletingId === request.id}
                        className="inline-flex items-center gap-1.5 rounded-md !border !border-[#e4d9c8] !bg-white px-3 py-1.5 text-[12px] font-medium !text-[#6b635c] !shadow-none transition hover:!border-[#fecdca] hover:!bg-[#fef3f2] hover:!text-[#b42318] hover:!translate-y-0 hover:!shadow-none disabled:opacity-60"
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        {deletingId === request.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(requestToDelete)}
        title="Delete request?"
        message={
          requestToDelete
            ? `Delete the VPS request from ${requestToDelete.fullName}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={Boolean(deletingId)}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default VpsHostingRequestsPage;
